import type {
  HTMLAttributes,
  ReactElement,
  ReactEventHandler,
  SyntheticEvent,
} from 'react';
import { cloneElement, useState } from 'react';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ButtonMain, InputLine } from '@quero-delivery/quero-components-web';
import { Modal } from '@querodelivery/uni-react';
import { isProd } from '@/_config/env';
import { isUrl } from '@/_utils/regex';

import styles from './styles.module.scss';
import { LOCALSTORAGE_URL_API_KEY } from '@/_constants/keys';

interface ModalChangeApiUrlProps {
  children: ReactElement;
}

interface ApiUrlForm {
  url: string;
}

const API_LOCALHOST = 'http://localhost:';
const API_STAGING = 'https://stageapi.querolog.io';

const handleEvents = (...callbacks: (ReactEventHandler | undefined)[]) => {
  return (event: SyntheticEvent) => {
    callbacks.forEach(callback => callback?.(event));
  };
};

export function ModalChangeApiUrl({ children }: ModalChangeApiUrlProps) {
  const { control, handleSubmit, setValue } = useForm<ApiUrlForm>({
    defaultValues: { url: '' },
  });

  const [countClickLogo, setCountClickLogo] = useState(0);
  const showModalApi = countClickLogo >= 5;

  const getClonedChildren = () => {
    if (isProd) {
      return children;
    }

    const newChildProps: HTMLAttributes<HTMLElement> & { key: string } = {
      key: 'modal-change-api-url',
    };

    newChildProps.onClick = handleEvents(children.props.onClick, () =>
      setCountClickLogo(value => value + 1),
    );

    return cloneElement(children, newChildProps);
  };

  const submitApiUrl: SubmitHandler<ApiUrlForm> = ({ url }) => {
    localStorage.setItem(
      LOCALSTORAGE_URL_API_KEY,
      JSON.stringify(url || API_STAGING),
    );
    handleReloadPage();
  };

  const submitErrors: SubmitErrorHandler<ApiUrlForm> = errors => {
    toast.warn(errors.url?.message || 'Url inválida');
  };

  const handleReloadPage = () => window.location.reload();

  return (
    <>
      {getClonedChildren()}

      <Modal
        isOpen={showModalApi && !isProd}
        onClose={() => setCountClickLogo(0)}
        header="API URL"
        width={480}
      >
        <form
          className={styles.containerModalApi}
          onSubmit={handleSubmit(submitApiUrl, submitErrors)}
        >
          <div className={styles.inputModalApi}>
            <div style={{ width: '100%' }}>
              <Controller
                name="url"
                control={control}
                rules={{ validate: isUrl }}
                render={({ field: { value, onChange } }) => (
                  <InputLine
                    label="Url"
                    placeholder={API_STAGING}
                    value={value}
                    onChange={onChange}
                    autoFocus
                  />
                )}
              />
            </div>
            <ButtonMain onClick={handleReloadPage} width="auto" type="submit">
              Atualizar
            </ButtonMain>
          </div>
          <div className={styles.buttonsModalApi}>
            <ButtonMain
              width="auto"
              secondary
              type="button"
              onClick={() => setValue('url', API_STAGING)}
            >
              Staging
            </ButtonMain>

            <ButtonMain
              width="auto"
              secondary
              type="button"
              onClick={() => setValue('url', API_LOCALHOST)}
            >
              LocalHost
            </ButtonMain>
          </div>
        </form>
      </Modal>
    </>
  );
}
